/* eslint-disable no-restricted-syntax */
// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { API_URL } from 'Config';
import { getJson, postJson, patchJson, deleteRequest } from 'Utils/http';
import type { RootState } from 'Store';
import type * as GeoJSON from 'geojson';
import { CreateAnnotationResponse, DeleteAnnotationResponse, EditAnnotationResponse, GetAnnotationResponse, PanoApiResponse } from 'Utils/typings/esritypes';
import { CreateAnnotationSuccessAction, DeleteAnnotationSuccessAction, EditAnnotationSuccessAction, GetAllAnnotationSuccessAction, LoadPanoFeaturesSuccessAction } from './esriSplitViewerActions';

interface SignedUrlResponse {
  success: boolean;
  data: string;
}

export interface PanoNeighbors {
  next_filename: string | null;
  prev_filename: string | null;
  left_filename: string | null;
  right_filename: string | null;
}

export const LoadPanoFeaturesThunk = () =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { projectId } = getState().project;
    const { token } = getState().auth;
    const url = `${API_URL}/pano/getGeojson/${projectId}`;
    const result = await getJson<PanoApiResponse>(url, token);
    if (result.success && result.data) {
      dispatch(LoadPanoFeaturesSuccessAction(result.data.features));
    }
  };

export const getSignedURLThunk = (filepath: string) =>
  async (dispatch: any, getState: () => RootState) => {
    const { token } = getState().auth;
    const url = `${API_URL}/pano/getImage?file=${filepath}`;
    const result = await getJson<SignedUrlResponse>(url, token);
    if (result.success && result.data) {
      return result.data;
    }
    return null;
  };

export const getPanoNeighborsThunk = (filename: string, heading?: number | null) =>
  async (dispatch: any, getState: () => RootState): Promise<PanoNeighbors | null> => {
    const { projectId } = getState().project;
    const { token } = getState().auth;
    const headingParam = typeof heading === 'number' && Number.isFinite(heading)
      ? `&heading=${heading}`
      : '';
    const url = `${API_URL}/pano/neighbors/${projectId}?filename=${encodeURIComponent(filename)}${headingParam}`;
    const result = await getJson<PanoNeighbors>(url, token);
    if (result.success && result.data) {
      return result.data;
    }
    return null;
  };

export const GetAllAnnotationThunk = () =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { token } = getState().auth;
    const orderList = getState().order.list;
    const list: {} = {};
    for (const ord of orderList) {
      const orgId = ord.ownerOrganization;
      const orderId = ord._id;
      const projectId = ord.ownerProject;
      const url = `${API_URL}/annotations/${orgId}/${projectId}/${orderId}`;
      // eslint-disable-next-line no-await-in-loop
      const result = await getJson<GetAnnotationResponse>(url, token);
      if (result.success && result.data) {
        list[orderId] = result.data;
      }
    }
    dispatch(GetAllAnnotationSuccessAction(list));
  };

export const CreateAnnotationThunk = (createEement) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { token } = getState().auth;
    const createElement = createEement;
    const url = `${API_URL}/annotations`;
    const result = await postJson<CreateAnnotationResponse>(url, createElement, token);
    if (result.success && result.data) {
      dispatch(CreateAnnotationSuccessAction(result.data));
    }
    return result;
  };

export const EditAnnotationThunk = (updatedAttributes, id) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { token } = getState().auth;
    const EditElement = updatedAttributes;
    const EditId = id;
    const EditUrl = `${API_URL}/annotations/${EditId}`;
    const result = await patchJson<EditAnnotationResponse>(EditUrl, EditElement, token);
    if (result.success && result.data) {
      dispatch(EditAnnotationSuccessAction(result.data));
    }
    return result;
  };

export const DeleteAnnotationThunk = (id, orderId) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { token } = getState().auth;
    const DeleteId = id;
    const Deleteurl = `${API_URL}/annotations/${DeleteId}`;
    const result = await deleteRequest<DeleteAnnotationResponse>(Deleteurl, token);
    if (result.success && result.data) {
      const orderObj = {};
      orderObj[orderId] = DeleteId;
      dispatch(DeleteAnnotationSuccessAction(orderObj));
    }
    return result;
  };
